import React, {Fragment, PropsWithChildren, useEffect, useImperativeHandle, useState} from "react";
import DrawerHandler from "@app/component/overlays/drawer/DrawerHandler";
import {XMarkIcon} from "@heroicons/react/20/solid";
import {Dialog, Transition} from '@headlessui/react'
import Utilities from "@util/Utilities";
import FormButton from "@app/component/form/button/FormButton";

type Props = {
    onClose?: any,
    title: string
    size?: 'small' | 'normal' | 'large' | 'huge'
    actions?: any,
    isLoading?: boolean,
    noCloseOnOverlayClick?: boolean,
    zIndexClass?: string,
    ref: any,

}

const Drawer: React.FunctionComponent<PropsWithChildren<Props>> = React.forwardRef(({
                                                                                        children,
                                                                                        title,
                                                                                        size = 'normal',
                                                                                        onClose,
                                                                                        isLoading,
                                                                                        actions,
                                                                                        noCloseOnOverlayClick = false,
                                                                                        zIndexClass  = 'z-10'
                                                                                    }, ref: React.Ref<DrawerHandler>) => {

    const [isActive, setIsActive] = useState<boolean>(false);
    const [sizeClassNames, setSizeClassNames] = useState<string>('');

    useEffect(() => {
        switch (size) {
            case 'small': {
                setSizeClassNames("max-w-md")
                break;
            }
            case 'normal': {
                setSizeClassNames("max-w-lg")
                break;
            }
            case 'large': {
                setSizeClassNames("max-w-2xl")
                break;
            }
            case 'huge': {
                setSizeClassNames("max-w-4xl")
                break;
            }
        }
    }, [])

    const open = () => {
        setIsActive(true);
    };
    const close = () => {
        setIsActive(false);
        if (!!onClose) onClose();
    };

    useImperativeHandle(ref, () => {
        return {
            open: open,
            close: close,
            isOpen: isActive
        }
    });

    return <Transition.Root show={isActive} as={Fragment}>
        <Dialog as="div" className={`relative ${zIndexClass}`} onClose={!noCloseOnOverlayClick ? close : ()=> {}}>
            <Transition.Child
                as={Fragment}
                enter="ease-in-out duration-500"
                enterFrom="opacity-0"
                enterTo="opacity-100"
                leave="ease-in-out duration-500"
                leaveFrom="opacity-100"
                leaveTo="opacity-0"
            >
                <div className="fixed inset-0 bg-slate-500 bg-opacity-75 transition-opacity"/>
            </Transition.Child>

            <div className="fixed inset-0 overflow-hidden">
                <div className="absolute inset-0 overflow-hidden">
                    <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10 sm:pl-16">
                        <Transition.Child
                            as={Fragment}
                            enter="transform transition ease-in-out duration-500 sm:duration-700"
                            enterFrom="translate-x-full"
                            enterTo="translate-x-0"
                            leave="transform transition ease-in-out duration-500 sm:duration-700"
                            leaveFrom="translate-x-0"
                            leaveTo="translate-x-full"
                        >
                            <Dialog.Panel
                                className={Utilities.classNames("pointer-events-auto w-screen", sizeClassNames)}>
                                <div className="flex h-full flex-col  bg-white shadow-xl">
                                    {/* Header */}
                                    <div className="flex-shrink-0 bg-slate-100 px-4 py-6 sm:px-6">
                                        <div className="flex items-start justify-between">
                                            <h2 id="slide-over-heading"
                                                className="text-base font-semibold leading-6 text-slate-900">
                                                {title}
                                            </h2>
                                            <div className="ml-3 flex h-7 items-center">
                                                <button
                                                  type="button"
                                                  className="rounded-md text-slate-400 hover:text-slate-500 focus:ring-2 focus:ring-blue-500"
                                                  onClick={close}
                                                >
                                                    <span className="sr-only">Close panel</span>
                                                    <XMarkIcon className="h-6 w-6" aria-hidden="true"/>
                                                </button>
                                            </div>
                                        </div>
                                    </div>

                                    <div className="flex-1 overflow-y-scroll">
                                        {/* Main Body */}
                                        {children}
                                    </div>
                                    {/* Action buttons */}
                                    <div className="flex-shrink-0 border-t border-slate-200 px-4 py-4 sm:px-6">
                                        <div className="flex justify-end space-x-3">
                                            <FormButton state='white' onClick={close} isLoading={isLoading}>
                                                Close
                                            </FormButton>
                                            {actions}
                                        </div>
                                    </div>
                                </div>
                            </Dialog.Panel>
                        </Transition.Child>
                    </div>
                </div>
            </div>
        </Dialog>
    </Transition.Root>
});

export default Drawer;
